import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Clock, Home, Building2, Star, X, Loader2, Navigation, Globe, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyDpEDi1ThZ_nQlppT0nUIRtFDUjg7yK_Y0';

/**
 * ZOLT Global Address Autocomplete Input Component
 * 
 * CORE INFRASTRUCTURE - Mission Critical
 * 
 * Features:
 * - WORLDWIDE address support (NO country restrictions by default)
 * - Google Places as PRIMARY provider
 * - OpenStreetMap Nominatim as FALLBACK
 * - Works logged in AND logged out
 * - Shows recent/saved addresses for logged-in users
 * - Always returns: formattedAddress, countryCode (ISO), lat, lng
 * - Provider tracking ("google" | "nominatim" | "manual")
 * 
 * Callback signature:
 * onAddressResolved({
 *   formattedAddress,
 *   placeId,
 *   lat,
 *   lng,
 *   postcode,
 *   city,
 *   countryCode,  // ISO 2-letter code (GB, US, IT, etc.)
 *   countryName,
 *   provider      // "google" | "nominatim" | "saved"
 * })
 */
const AddressAutocompleteInput = ({
  value = '',
  onAddressSelected,
  placeholder = 'Enter address, postcode, or city',
  countryRestriction = null,  // Optional: ISO code like 'gb', 'us', null for worldwide
  showRecentAddresses = true,
  showSavedAddresses = true,
  className = '',
  required = false,
  disabled = false,
  token = null,
  testId = 'address-autocomplete'
}) => {
  // State
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [recentAddresses, setRecentAddresses] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [error, setError] = useState(null);
  
  // Provider state
  const [googleAvailable, setGoogleAvailable] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);
  const [googleLoadFailed, setGoogleLoadFailed] = useState(false);
  
  // Refs
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const geocoder = useRef(null);
  const sessionToken = useRef(null);
  const searchTimeout = useRef(null);
  const googleLoadAttempted = useRef(false);

  // ============================================================
  // GOOGLE MAPS INITIALIZATION
  // ============================================================
  useEffect(() => {
    const loadGoogleMapsScript = () => {
      // Prevent duplicate load attempts
      if (googleLoadAttempted.current) return;
      googleLoadAttempted.current = true;

      // Check if already loaded
      if (window.google?.maps?.places) {
        initializeGoogleServices();
        return;
      }

      // Check if script is already loading
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        const checkLoaded = setInterval(() => {
          if (window.google?.maps?.places) {
            initializeGoogleServices();
            clearInterval(checkLoaded);
          }
        }, 100);
        
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkLoaded);
          if (!window.google?.maps?.places) {
            console.warn('Google Maps timeout - activating fallback');
            setGoogleLoadFailed(true);
            setUsingFallback(true);
          }
        }, 10000);
        return;
      }

      // Load script
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        if (window.google?.maps?.places) {
          initializeGoogleServices();
        } else {
          setGoogleLoadFailed(true);
          setUsingFallback(true);
        }
      };
      
      script.onerror = () => {
        console.warn('Google Maps script failed to load - activating fallback');
        setGoogleLoadFailed(true);
        setUsingFallback(true);
      };
      
      document.head.appendChild(script);
    };

    const initializeGoogleServices = () => {
      try {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        geocoder.current = new window.google.maps.Geocoder();
        
        // Create hidden div for PlacesService
        const mapDiv = document.createElement('div');
        mapDiv.style.display = 'none';
        mapDiv.id = 'zolt-places-service-div';
        if (!document.getElementById('zolt-places-service-div')) {
          document.body.appendChild(mapDiv);
        }
        placesService.current = new window.google.maps.places.PlacesService(mapDiv);
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        
        setGoogleAvailable(true);
        setUsingFallback(false);
        setGoogleLoadFailed(false);
      } catch (err) {
        console.error('Google Maps initialization failed:', err);
        setGoogleLoadFailed(true);
        setUsingFallback(true);
      }
    };

    loadGoogleMapsScript();
  }, []);

  // ============================================================
  // FETCH USER'S SAVED/RECENT ADDRESSES
  // ============================================================
  useEffect(() => {
    const fetchUserAddresses = async () => {
      if (!token) return;
      
      try {
        const response = await fetch(`${API_URL}/api/customer/addresses`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          const saved = data.filter(a => a.is_saved);
          const recent = data.filter(a => !a.is_saved).slice(0, 15);
          setSavedAddresses(saved);
          setRecentAddresses(recent);
        }
      } catch (err) {
        console.error('Failed to fetch addresses:', err);
      }
    };

    fetchUserAddresses();
  }, [token]);

  // Sync external value
  useEffect(() => {
    if (value !== inputValue && value) {
      setInputValue(value);
    }
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ============================================================
  // GOOGLE PLACES SEARCH (PRIMARY)
  // ============================================================
  const searchGooglePlaces = useCallback(async (query) => {
    if (!autocompleteService.current) return null;

    return new Promise((resolve) => {
      const request = {
        input: query,
        sessionToken: sessionToken.current,
        // NO componentRestrictions by default = WORLDWIDE
        // Only add if explicitly requested
        ...(countryRestriction && { componentRestrictions: { country: countryRestriction } })
      };

      autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          resolve(predictions.map(p => ({
            placeId: p.place_id,
            description: p.description,
            mainText: p.structured_formatting?.main_text,
            secondaryText: p.structured_formatting?.secondary_text,
            provider: 'google'
          })));
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          resolve([]);
        } else {
          // Google failed - trigger fallback
          console.warn('Google Places search failed, status:', status);
          resolve(null); // null indicates failure, [] indicates no results
        }
      });
    });
  }, [countryRestriction]);

  // ============================================================
  // NOMINATIM FALLBACK SEARCH
  // ============================================================
  const searchNominatim = useCallback(async (query) => {
    try {
      const params = new URLSearchParams({
        q: query,
        format: 'json',
        addressdetails: '1',
        limit: '8'
      });
      
      // Add country filter if specified
      if (countryRestriction) {
        params.append('countrycodes', countryRestriction);
      }

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?${params}`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'ZOLT-App/1.0'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Nominatim request failed');
      }

      const data = await response.json();
      
      return data.map(item => ({
        placeId: null, // Nominatim doesn't have Google place IDs
        nominatimId: item.place_id,
        description: item.display_name,
        mainText: item.name || item.display_name.split(',')[0],
        secondaryText: item.display_name.split(',').slice(1).join(',').trim(),
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
        countryCode: item.address?.country_code?.toUpperCase(),
        countryName: item.address?.country,
        postcode: item.address?.postcode,
        city: item.address?.city || item.address?.town || item.address?.village || item.address?.municipality,
        provider: 'nominatim'
      }));
    } catch (err) {
      console.error('Nominatim search failed:', err);
      return [];
    }
  }, [countryRestriction]);

  // ============================================================
  // UNIFIED SEARCH (tries Google, falls back to Nominatim)
  // ============================================================
  const searchAddresses = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let results = null;

      // Try Google first if available
      if (googleAvailable && !googleLoadFailed) {
        results = await searchGooglePlaces(query);
        
        if (results === null) {
          // Google failed - switch to fallback
          console.warn('Google returned null - switching to Nominatim fallback');
          setUsingFallback(true);
          results = await searchNominatim(query);
        }
      } else {
        // Use fallback directly
        results = await searchNominatim(query);
      }

      setSuggestions(results || []);
    } catch (err) {
      console.error('Address search error:', err);
      setError('Search temporarily unavailable');
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [googleAvailable, googleLoadFailed, searchGooglePlaces, searchNominatim]);

  // Debounced search trigger
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (inputValue && inputValue.length >= 2) {
      searchTimeout.current = setTimeout(() => {
        searchAddresses(inputValue);
      }, 300);
    }

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [inputValue, searchAddresses]);

  // ============================================================
  // GET GOOGLE PLACE DETAILS
  // ============================================================
  const getGooglePlaceDetails = async (placeId) => {
    if (!placesService.current) return null;

    return new Promise((resolve, reject) => {
      placesService.current.getDetails(
        {
          placeId: placeId,
          fields: ['formatted_address', 'geometry', 'address_components', 'place_id', 'name']
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            // Parse address components for country code
            const components = place.address_components || [];
            let postcode = '', city = '', countryCode = '', countryName = '';
            
            components.forEach(comp => {
              if (comp.types.includes('postal_code')) postcode = comp.long_name;
              if (comp.types.includes('locality') || comp.types.includes('postal_town')) city = comp.long_name;
              if (comp.types.includes('administrative_area_level_1') && !city) city = comp.long_name;
              if (comp.types.includes('country')) {
                countryCode = comp.short_name; // ISO code (GB, US, IT, etc.)
                countryName = comp.long_name;
              }
            });

            // Create new session token
            sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();

            resolve({
              formattedAddress: place.formatted_address,
              placeId: place.place_id,
              lat: place.geometry?.location?.lat(),
              lng: place.geometry?.location?.lng(),
              postcode,
              city,
              countryCode,
              countryName,
              provider: 'google'
            });
          } else {
            reject(new Error('Failed to get place details'));
          }
        }
      );
    });
  };

  // ============================================================
  // HANDLE SUGGESTION SELECTION
  // ============================================================
  const handleSelectSuggestion = async (suggestion) => {
    setIsLoading(true);
    setError(null);
    
    try {
      let addressData;

      if (suggestion.provider === 'google' && suggestion.placeId) {
        // Get details from Google
        addressData = await getGooglePlaceDetails(suggestion.placeId);
      } else {
        // Nominatim suggestion already has all data
        addressData = {
          formattedAddress: suggestion.description,
          placeId: null,
          lat: suggestion.lat,
          lng: suggestion.lng,
          postcode: suggestion.postcode || '',
          city: suggestion.city || '',
          countryCode: suggestion.countryCode || '',
          countryName: suggestion.countryName || '',
          provider: 'nominatim'
        };
      }

      if (addressData && addressData.lat && addressData.lng) {
        setInputValue(addressData.formattedAddress);
        setSelectedAddress(addressData);
        setSuggestions([]);
        setShowDropdown(false);
        
        if (onAddressSelected) {
          onAddressSelected(addressData);
        }
      } else {
        setError('Could not resolve address location');
      }
    } catch (err) {
      console.error('Selection error:', err);
      
      // Try Nominatim as emergency fallback
      if (suggestion.provider === 'google') {
        try {
          const fallbackResults = await searchNominatim(suggestion.description);
          if (fallbackResults.length > 0) {
            const fallback = fallbackResults[0];
            const addressData = {
              formattedAddress: fallback.description,
              placeId: null,
              lat: fallback.lat,
              lng: fallback.lng,
              postcode: fallback.postcode || '',
              city: fallback.city || '',
              countryCode: fallback.countryCode || '',
              countryName: fallback.countryName || '',
              provider: 'nominatim'
            };
            
            setInputValue(addressData.formattedAddress);
            setSelectedAddress(addressData);
            setSuggestions([]);
            setShowDropdown(false);
            setUsingFallback(true);
            
            if (onAddressSelected) {
              onAddressSelected(addressData);
            }
            return;
          }
        } catch (fallbackErr) {
          console.error('Fallback also failed:', fallbackErr);
        }
      }
      
      setError('Failed to get address details');
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================
  // HANDLE SAVED/RECENT ADDRESS SELECTION
  // ============================================================
  const handleSelectSavedAddress = (address) => {
    const addressData = {
      formattedAddress: address.formatted_address,
      placeId: address.place_id || null,
      lat: address.lat,
      lng: address.lng,
      postcode: address.postcode || '',
      city: address.city || '',
      countryCode: address.country_code || '',
      countryName: address.country || '',
      provider: 'saved'
    };

    setInputValue(address.formatted_address);
    setSelectedAddress(addressData);
    setShowDropdown(false);

    if (onAddressSelected) {
      onAddressSelected(addressData);
    }
  };

  // ============================================================
  // USE CURRENT LOCATION
  // ============================================================
  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported by your browser');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000
        });
      });

      const { latitude, longitude } = position.coords;
      let addressData = null;

      // Try Google reverse geocoding first
      if (googleAvailable && geocoder.current) {
        try {
          const result = await new Promise((resolve, reject) => {
            geocoder.current.geocode(
              { location: { lat: latitude, lng: longitude } },
              (results, status) => {
                if (status === 'OK' && results[0]) {
                  resolve(results[0]);
                } else {
                  reject(new Error('Google geocoding failed'));
                }
              }
            );
          });

          const components = result.address_components || [];
          let postcode = '', city = '', countryCode = '', countryName = '';
          
          components.forEach(comp => {
            if (comp.types.includes('postal_code')) postcode = comp.long_name;
            if (comp.types.includes('locality') || comp.types.includes('postal_town')) city = comp.long_name;
            if (comp.types.includes('country')) {
              countryCode = comp.short_name;
              countryName = comp.long_name;
            }
          });

          addressData = {
            formattedAddress: result.formatted_address,
            placeId: result.place_id,
            lat: latitude,
            lng: longitude,
            postcode,
            city,
            countryCode,
            countryName,
            provider: 'google'
          };
        } catch (googleErr) {
          console.warn('Google reverse geocode failed, trying Nominatim');
        }
      }

      // Nominatim fallback for reverse geocoding
      if (!addressData) {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
          {
            headers: {
              'Accept': 'application/json',
              'User-Agent': 'ZOLT-App/1.0'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          addressData = {
            formattedAddress: data.display_name,
            placeId: null,
            lat: latitude,
            lng: longitude,
            postcode: data.address?.postcode || '',
            city: data.address?.city || data.address?.town || data.address?.village || '',
            countryCode: data.address?.country_code?.toUpperCase() || '',
            countryName: data.address?.country || '',
            provider: 'nominatim'
          };
          setUsingFallback(true);
        }
      }

      if (addressData) {
        setInputValue(addressData.formattedAddress);
        setSelectedAddress(addressData);
        setShowDropdown(false);

        if (onAddressSelected) {
          onAddressSelected(addressData);
        }
      } else {
        setError('Could not determine your location');
      }
    } catch (err) {
      console.error('Geolocation error:', err);
      if (err.code === 1) {
        setError('Location access denied');
      } else if (err.code === 2) {
        setError('Location unavailable');
      } else if (err.code === 3) {
        setError('Location request timed out');
      } else {
        setError('Failed to get your location');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================
  // INPUT HANDLERS
  // ============================================================
  const handleFocus = () => {
    setShowDropdown(true);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedAddress(null);
    setShowDropdown(true);
    setError(null);
    
    if (!newValue) {
      setSuggestions([]);
    }
  };

  const handleClear = () => {
    setInputValue('');
    setSelectedAddress(null);
    setSuggestions([]);
    setError(null);
    
    if (onAddressSelected) {
      onAddressSelected(null);
    }
  };

  // Handle blur - force resolution if user typed but didn't select
  const handleBlur = async () => {
    // Small delay to allow click on dropdown
    setTimeout(async () => {
      if (inputValue && !selectedAddress && suggestions.length > 0) {
        // Auto-select first suggestion
        await handleSelectSuggestion(suggestions[0]);
      } else if (inputValue && !selectedAddress && suggestions.length === 0) {
        // Try to resolve via Nominatim
        const results = await searchNominatim(inputValue);
        if (results.length > 0) {
          await handleSelectSuggestion(results[0]);
        }
      }
    }, 200);
  };

  // ============================================================
  // RENDER HELPERS
  // ============================================================
  const hasDropdownContent = showDropdown && (
    suggestions.length > 0 ||
    (token && showSavedAddresses && savedAddresses.length > 0) ||
    (token && showRecentAddresses && recentAddresses.length > 0) ||
    (!inputValue && (googleAvailable || usingFallback))
  );

  const getProviderIcon = (provider) => {
    if (provider === 'nominatim') {
      return <Globe className="w-4 h-4 text-blue-500" />;
    }
    return <MapPin className="w-4 h-4 text-neutral-400" />;
  };

  return (
    <div className="relative" data-testid={testId}>
      {/* Fallback indicator banner */}
      {usingFallback && !error && (
        <div className="mb-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-xs text-blue-700">
          <Globe className="w-3.5 h-3.5" />
          <span>Using backup location service</span>
        </div>
      )}

      {/* Input Field */}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400 pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={cn(
            'pl-10 pr-10 h-12 text-base',
            selectedAddress && 'border-green-500 bg-green-50',
            error && 'border-red-500',
            className
          )}
          data-testid={`${testId}-input`}
        />
        
        {/* Loading/Clear indicators */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isLoading && (
            <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
          )}
          {inputValue && !isLoading && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-neutral-100 rounded-full"
              data-testid={`${testId}-clear`}
            >
              <X className="w-4 h-4 text-neutral-400" />
            </button>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mt-1 flex items-center gap-1 text-red-500 text-xs">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Selected address indicator */}
      {selectedAddress && (
        <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
          <MapPin className="w-4 h-4" />
          <span>
            Location confirmed • {selectedAddress.city || selectedAddress.postcode}
            {selectedAddress.countryCode && ` (${selectedAddress.countryCode})`}
          </span>
          {selectedAddress.provider === 'nominatim' && (
            <span className="text-xs text-blue-500">(backup)</span>
          )}
        </div>
      )}

      {/* Dropdown */}
      {hasDropdownContent && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-xl shadow-lg max-h-96 overflow-auto"
          data-testid={`${testId}-dropdown`}
        >
          {/* Use Current Location */}
          {!inputValue && (googleAvailable || usingFallback) && (
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-neutral-50 text-left border-b border-neutral-100"
              data-testid={`${testId}-current-location`}
            >
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <Navigation className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <div className="font-medium text-sm">Use current location</div>
                <div className="text-xs text-neutral-500">Get your precise location</div>
              </div>
            </button>
          )}

          {/* Saved Addresses */}
          {token && showSavedAddresses && savedAddresses.length > 0 && !inputValue && (
            <div className="border-b border-neutral-100">
              <div className="px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide bg-neutral-50">
                Saved Addresses
              </div>
              {savedAddresses.map((addr) => (
                <button
                  key={addr.id}
                  type="button"
                  onClick={() => handleSelectSavedAddress(addr)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-neutral-50 text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                    {addr.label?.toLowerCase() === 'home' ? (
                      <Home className="w-4 h-4 text-neutral-600" />
                    ) : addr.label?.toLowerCase() === 'work' ? (
                      <Building2 className="w-4 h-4 text-neutral-600" />
                    ) : (
                      <Star className="w-4 h-4 text-neutral-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{addr.label || 'Saved'}</div>
                    <div className="text-xs text-neutral-500 truncate">{addr.formatted_address}</div>
                  </div>
                  {addr.country_code && (
                    <span className="text-xs text-neutral-400">{addr.country_code}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Recent Addresses */}
          {token && showRecentAddresses && recentAddresses.length > 0 && !inputValue && (
            <div className="border-b border-neutral-100">
              <div className="px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide bg-neutral-50">
                Recent
              </div>
              {recentAddresses.slice(0, 5).map((addr) => (
                <button
                  key={addr.id}
                  type="button"
                  onClick={() => handleSelectSavedAddress(addr)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-neutral-50 text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-neutral-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{addr.formatted_address}</div>
                    <div className="text-xs text-neutral-500">
                      {addr.city}{addr.postcode && `, ${addr.postcode}`}
                    </div>
                  </div>
                  {addr.country_code && (
                    <span className="text-xs text-neutral-400">{addr.country_code}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Address Suggestions */}
          {suggestions.length > 0 && (
            <div>
              {inputValue && (
                <div className="px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide bg-neutral-50 flex items-center justify-between">
                  <span>Suggestions</span>
                  {usingFallback && (
                    <span className="text-blue-500 flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      OpenStreetMap
                    </span>
                  )}
                </div>
              )}
              {suggestions.map((suggestion, index) => (
                <button
                  key={suggestion.placeId || `nominatim-${index}`}
                  type="button"
                  onClick={() => handleSelectSuggestion(suggestion)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-neutral-50 text-left"
                  data-testid={`${testId}-suggestion-${index}`}
                >
                  <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                    {getProviderIcon(suggestion.provider)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{suggestion.mainText}</div>
                    <div className="text-xs text-neutral-500 truncate">{suggestion.secondaryText}</div>
                  </div>
                  {suggestion.countryCode && (
                    <span className="text-xs text-neutral-400 font-medium">{suggestion.countryCode}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* No results */}
          {inputValue && inputValue.length >= 2 && suggestions.length === 0 && !isLoading && !error && (
            <div className="px-4 py-6 text-center text-neutral-500 text-sm">
              No addresses found. Try a different search.
            </div>
          )}

          {/* Attribution */}
          <div className="px-4 py-2 border-t border-neutral-100 bg-neutral-50 flex items-center justify-between">
            {usingFallback ? (
              <div className="flex items-center gap-2 text-xs text-neutral-500">
                <Globe className="w-3.5 h-3.5" />
                <span>© OpenStreetMap contributors</span>
              </div>
            ) : (
              <img 
                src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png" 
                alt="Powered by Google" 
                className="h-4"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocompleteInput;
